import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { EditorState, Transaction } from '@tiptap/pm/state';
import { isTextSelection } from '@tiptap/react';

export interface DaIndentOptions {
  /* Bind the tab shift+tab keys */
  captureTab: boolean;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indentation: {
      applyIndentation: (
        direction: number,
        type: 'block' | 'line'
      ) => ReturnType;
      /**
       * Increase text indent
       */
      indent: (type: 'block' | 'line') => ReturnType;
      /**
       * Decrease text indent
       */
      outdent: (type: 'block' | 'line') => ReturnType;
    };
  }
}
const isInsideList = (state: EditorState) => {
  let listNode = false;

  const { from, to } = state.selection;

  state.doc.nodesBetween(from, to, (node: Node) => {
    if (['listItem', 'bulletList', 'orderedList'].includes(node.type.name)) {
      listNode = true;
    }
  });

  return listNode;
};

const INDENT_SIZE = 24;
const minLevel = 0;
const maxLevel = 10;
const typesThatCanBeIndented = ['paragraph', 'heading'];

export const Indent = Extension.create<DaIndentOptions>({
  name: 'da-indent',
  groups: ['shortcuts-enabled'],

  addGlobalAttributes() {
    return [
      {
        types: typesThatCanBeIndented,
        attributes: {
          indentType: {
            renderHTML: attributes => ({}),
          },
          indentation: {
            renderHTML: attributes => {
              if (
                attributes.indentation === 0 ||
                Number.isNaN(parseInt(attributes.indentation, 10))
              ) {
                return {};
              }

              if (attributes.indentType === 'line') {
                return {
                  style: `text-indent: ${
                    attributes.indentation * INDENT_SIZE
                  }px`,
                };
              }

              return {
                style: `margin-inline-start: ${
                  attributes.indentation * INDENT_SIZE
                }px`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    const setNodeIndentMarkup = (
      tr: Transaction,
      pos: number,
      delta: number,
      type
    ): Transaction => {
      const node = tr?.doc?.nodeAt(pos);

      if (node) {
        const nextLevel = (node.attrs.indentation || 0) + delta;
        const indent =
          nextLevel < minLevel
            ? minLevel
            : nextLevel > maxLevel
            ? maxLevel
            : nextLevel;

        if (indent !== node.attrs.indentation) {
          const { indentation: _, ...currentAttrs } = node.attrs;
          const nodeAttrs =
            indent > minLevel
              ? { ...currentAttrs, indentation: indent, indentType: type }
              : currentAttrs;
          return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
        }
      }
      return tr;
    };

    const updateIndentationLevel = (
      tr: Transaction,
      delta: number,
      type
    ): Transaction => {
      const { doc, selection } = tr;
      if (doc && selection && isTextSelection(selection)) {
        const { from, to } = selection;
        doc.nodesBetween(from, to, (node, pos) => {
          if (typesThatCanBeIndented.includes(node.type.name)) {
            setNodeIndentMarkup(tr, pos, delta, type);
            return false;
          }

          return true;
        });
      }

      return tr;
    };

    return {
      applyIndentation:
        (direction, type) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          tr.setSelection(selection);
          updateIndentationLevel(tr, direction, type);

          if (tr.docChanged) {
            dispatch?.(tr);
            return true;
          }

          return false;
        },

      indent:
        type =>
        ({ state, commands }) => {
          if (isInsideList(state)) {
            return false;
          }
          return commands.applyIndentation(1, type);
        },
      outdent:
        type =>
        ({ state, commands }) => {
          if (isInsideList(state)) {
            return false;
          }
          return commands.applyIndentation(-1, type);
        },
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => this.options.captureTab && this.editor.commands.indent('line'),
      'Shift-Tab': () =>
        this.options.captureTab && this.editor.commands.outdent('line'),
    };
  },
});
